import * as yup from 'yup';
import { useEffect, useState } from 'react';
import Visibility from "@mui/icons-material/Visibility";
import { useNavigate, useParams } from 'react-router-dom';
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
    Box,
    Grid,
    LinearProgress,
    Paper,
    Typography,
    InputAdornment,
    IconButton,
    Button
} from '@mui/material';

import { IUser } from '../../shared/protocolos';
import { DetailTool } from '../../shared/components';
import { Alerts } from '../../shared/components/alerts'
import { LayoutBaseDePagina } from '../../shared/layouts';
import { useAuthContext } from '../../shared/contexts/AuthContext';
import { AutoCompleteStatus } from './components/AutoCompleteStatus';
import { UserService } from '../../shared/services/user/userService';
import { AutoCompleteIsAdmin } from './components/AutoCompleteIsAdmin';
import { VTextField, VForm, useVForm, IVFormErrors } from '../../shared/forms';

const formValidationSchema: yup.Schema<IUser> = yup.object().shape({
    name: yup.string().required().min(3),
    email: yup.string().required(),
    password: yup.string(),
    cpf: yup.string().required().min(3),
    telefone: yup.string().optional().min(3),
    status: yup.string().required().min(3),
    isAdmin: yup.boolean().required(),
    role: yup.string().required(),
    endereco: yup.string().optional().min(3),
    bairro: yup.string().optional().min(3),
    cep: yup.string().optional().min(8).max(8),
    cidade: yup.string().optional().min(3),
    uf: yup.string().optional().min(2),
    avatar: yup.string(),
    createdBy: yup.string().required(),
});

export const UpsertUser: React.FC = () => {
    const navigate = useNavigate();
    const { id = 'nova' } = useParams<'id'>();
    const userId = localStorage.getItem('LOGGED_USER_ID');
    const { formRef, save, saveAndClose, isSaveAndClose } = useVForm();

    const [alertType, setAlertType] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [editPassword, setEditPassword] = useState(false);
    const [originalEmail, setOriginalEmail] = useState('');

    const { userLogged } = useAuthContext();

    const isUserDeletedNeedsReactivation = (result: any) => {
        return result?.code === 'USER_DELETED_NEEDS_REACTIVATION' ||
            result?.code === 'ACCOUNT_DEACTIVATED';
    };

    const isUser = (result: any): result is IUser => {
        return !!result?.id;
    };

    useEffect(() => {
        if (id !== 'nova') {
            setIsLoading(true);

            UserService.getById(id).then((result) => {
                setIsLoading(false);

                if (result instanceof Error) {
                    alert(result.message);
                    navigate('/user');
                } else {
                    const user = result as IUser;
                    setOriginalEmail((user.email ?? '').toLowerCase());

                    formRef.current?.setData({
                        name: user.name ?? "",
                        email: user.email ?? "",
                        emailVerifiedAt: (user as any).emailVerifiedAt ?? "",
                        password: "",
                        cpf: user.cpf ?? "",
                        telefone: user.telefone ?? "",
                        status: user.status ?? "",
                        isAdmin: user.role === "ADMIN",
                        role: user.role ?? "",
                        endereco: user.endereco ?? "",
                        bairro: user.bairro ?? "",
                        cep: user.cep ?? "",
                        cidade: user.cidade ?? "",
                        uf: user.uf ?? "",
                        avatar: user.avatar ?? "",
                        createdBy: user.createdBy ?? userLogged.id,
                    });
                }
            });
        } else {
            formRef.current?.setData({
                name: "",
                email: "",
                emailVerifiedAt: "",
                password: "",
                cpf: "",
                telefone: "",
                status: "",
                endereco: "",
                bairro: "",
                cep: "",
                cidade: "",
                uf: "",
            });
        }
    }, [id, formRef, navigate]);

    const handleReactivateByCpf = (dadosValidados: IUser, result: any) => {
        const confirmReactivate = window.confirm(
            `${result.message || 'Existe um usuário excluído com este CPF.'}\n\n` +
            `Nome: ${result.name || dadosValidados.name}\n` +
            `CPF: ${result.cpf || dadosValidados.cpf}\n` +
            `E-mail: ${result.email || dadosValidados.email}\n\n` +
            `Deseja reativar este cadastro?`
        );

        if (!confirmReactivate) {
            return;
        }

        setIsLoading(true);

        UserService.reactivateByCpf(
            dadosValidados.cpf,
            userId as string
        ).then((reactivateResult) => {
            setIsLoading(false);

            if (reactivateResult instanceof Error) {
                setAlertMessage(reactivateResult.message);
                setAlertType(1);
                return;
            }

            alert('Usuário reativado com sucesso!');
            navigate('/user');
        });
    };

    const handleSave = (dados: IUser) => {
        dados.email = dados.email.trim().toLowerCase();

        if (id !== 'nova' && dados.email !== originalEmail) {
            const confirmed = window.confirm(
                `Confirma a alteração do e-mail para ${dados.email}?\n\n` +
                'O e-mail anterior deixará de ser válido e uma nova confirmação será enviada.'
            );

            if (!confirmed) return;
        }

        dados.createdBy = userLogged.id;
        dados.avatar = dados.avatar ?? "";
        dados.role = dados.role ?? "";
        dados.isAdmin = dados.role === "ADMIN";

        dados.telefone = dados.telefone ?? "";
        dados.endereco = dados.endereco ?? "";
        dados.bairro = dados.bairro ?? "";
        dados.uf = dados.uf ?? "";
        dados.cidade = dados.cidade ?? "";
        dados.cep = dados.cep ?? "";

        formValidationSchema
            .validate(dados, {
                abortEarly: false,
                context: { isUpdate: id !== 'nova' }
            })
            .then(async (dadosValidados) => {
                setIsLoading(true);

                if (id === 'nova') {
                    UserService.create(dadosValidados).then((result) => {
                        setIsLoading(false);

                        if (isUserDeletedNeedsReactivation(result)) {
                            handleReactivateByCpf(dadosValidados, result);
                            return;
                        }

                        if (result instanceof Error) {
                            setAlertMessage(result.message);
                            setAlertType(1);
                            return;
                        }

                        if (isUser(result)) {
                            if (isSaveAndClose()) {
                                navigate('/user');
                            } else {
                                navigate(`/user/detalhe/${result.id}`);
                            }
                        }
                    });

                    return;
                }

                if (!editPassword) {
                    dadosValidados.password = "";
                }

                UserService.update(id, dadosValidados).then((result) => {
                    setIsLoading(false);

                    if (result instanceof Error) {
                        setAlertMessage(result.message);
                        setAlertType(1);
                    } else {
                        setOriginalEmail(dadosValidados.email);
                        setAlertMessage("Usuário Atualizado com Sucesso");
                        setAlertType(4);

                        if (isSaveAndClose()) {
                            navigate('/user');
                        }
                    }
                });
            })
            .catch((errors: yup.ValidationError) => {
                setAlertMessage("Campos Inválidos");
                setAlertType(2);

                const validationErrors: IVFormErrors = {};

                errors.inner.forEach(error => {
                    if (!error.path) return;
                    validationErrors[error.path] = error.message;
                });

                formRef.current?.setErrors(validationErrors);
            });
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Deseja desabilitar temporariamente este usuário? O cadastro poderá ser reativado depois.')) {
            await UserService.disableById(id).then(result => {
                if (result instanceof Error) {
                    alert(result.message);
                } else {
                    alert('Usuário desabilitado com sucesso!');
                    navigate("/user");
                }
            });
        }
    };

    const renderPasswordField = () => {
        if (id === 'nova' || editPassword) {
            return (
                <VTextField
                    fullWidth
                    name='password'
                    label={id === 'nova' ? 'Senha' : 'Nova Senha'}
                    type={showPassword ? 'text' : 'password'}
                    variant="outlined"
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={handleShowPassword} edge="end">
                                    {showPassword ? <Visibility /> : <VisibilityOff />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    disabled={isLoading}
                />
            );
        }

        return (
            <Box display="flex" alignItems="center" height="100%">
                <Button onClick={() => setEditPassword(true)} variant="outlined">
                    Alterar senha
                </Button>
            </Box>
        );
    };

    const handleShowPassword = () => setShowPassword(prev => !prev);

    return (
        <>
            {alertType !== 0 && (
                <Box position='fixed' top='50px' right='10px'>
                    <Alerts mensagem={alertMessage} type={alertType} />
                </Box>
            )}

            <LayoutBaseDePagina
                barraDeFerramentas={
                    <DetailTool
                        textoBotaoNovo='Nova'
                        mostrarBotaoSalvarEFechar
                        mostrarBotaoNovo={id !== 'nova'}
                        mostrarBotaoApagar={id !== 'nova'}
                        aoClicarEmSalvar={save}
                        aoClicarEmSalvarEFechar={saveAndClose}
                        aoClicarEmApagar={() => handleDelete(id)}
                        aoClicarEmVoltar={() => navigate('/user')}
                        aoClicarEmNovo={() => navigate('/user/detalhe/nova')}
                    />
                }
            >
                <VForm ref={formRef} onSubmit={handleSave}>
                    <Box margin={1} component={Paper} variant="outlined" padding={2}>
                        {isLoading && (
                            <LinearProgress variant='indeterminate' />
                        )}

                        <Typography variant='h6' gutterBottom>
                            {id === 'nova' ? 'Adicionar Usuário' : 'Atualizar Usuário'}
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField name='name' label='Nome' fullWidth disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField
                                    name='email'
                                    label='E-mail'
                                    type='email'
                                    fullWidth
                                    disabled={isLoading}
                                    onChange={(event) => {
                                        event.target.value = event.target.value.toLowerCase().replace(/\s/g, '');
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField name='cpf' label='CPF' fullWidth disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField name='telefone' label='Telefone' fullWidth disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <AutoCompleteStatus />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <AutoCompleteIsAdmin />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField name='endereco' label='Endereço' fullWidth disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField name='bairro' label='Bairro' fullWidth disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField name='cep' label='CEP' fullWidth disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField name='uf' label='UF' fullWidth disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <VTextField name='cidade' label='Cidade' fullWidth disabled={isLoading} />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                {renderPasswordField()}
                            </Grid>
                        </Grid>
                    </Box>
                </VForm>
            </LayoutBaseDePagina>
        </>
    );
};
